import { useMemo, useState } from 'react';
import { acceptInvitation } from '../../credential/api';
import { LINK_PROBLEMS, tokenFromLink } from '../../credential/link';
import { passwordProblem } from '../../credential/password-rule';
import { CredentialShell, Refused } from './CredentialShell';
import { PasswordField } from './PasswordField';

/**
 * `/invitation`, with its token parameter: the page an invitation email lands on
 * (docs/DECISIONS.md ZD-118 §1, §2).
 *
 * Accepting sets the password and signs the person in — the API answers with
 * the cookie — so success goes straight to the workspace. A refused token is
 * the API's one sentence plus the one thing a person can do about it, which
 * the email already told them: ask the person who administers their access.
 */
export function InvitationPage(): React.JSX.Element {
  const link = useMemo(() => tokenFromLink(window.location.search), []);
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  if (link.kind !== 'present') {
    return (
      <CredentialShell title="Set your password">
        <p className="cred__reason">{LINK_PROBLEMS[link.kind]}</p>
      </CredentialShell>
    );
  }

  const submit = async (): Promise<void> => {
    setRefusal(null);
    const rule = passwordProblem(password);
    setProblem(rule);
    if (rule !== null) return;
    setSending(true);
    try {
      const outcome = await acceptInvitation(link.token, password);
      if (!outcome.ok) {
        setRefusal(outcome.message);
        return;
      }
      setPassword('');
      window.location.assign('/');
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <CredentialShell title="Set your password">
      <p className="cred__lead">
        You have been given access to Zevik. Choose the password you will sign in with; this link
        works once.
      </p>
      <form
        className="cred__form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <PasswordField value={password} onChange={setPassword} />
        {problem === null ? null : <Refused message={problem} />}
        {refusal === null ? null : (
          <>
            <Refused message={refusal} />
            <p className="cred__foot">
              An invitation link works once and for a limited time. Ask the person who administers
              your Zevik access to send a new one.
            </p>
          </>
        )}
        <div className="cred__actions">
          <button type="submit" className="submit" disabled={sending}>
            {sending ? 'Setting…' : 'Set password and sign in'}
          </button>
        </div>
      </form>
    </CredentialShell>
  );
}
